const state = () => ({
  devLogin: {},
  requestData: {},
  completed: {},
  active: [],
  visibleCounter: {
    completed: 1,
    active: 1
  },
  homeworks: {
    pending: {},
    completed: {},
    withoutCheck: {}
  },
  selectedHomeworksType: 'pending',
  homeworksPagination: {
    page: 1,
    limit: 10,
    limitOptions: [10, 25, 50]
  },
  homeworksSearchFields: {
    courses: [],
    streams: [],
    lessons: []
  },
  homeworksSearchOptions: [],
  homeworksQueryParams: {
    limit: 10,
    search: null,
    course_id: null,
    stream_id: null,
    lesson_id: null,
    sort: null,
    sort_method: null
  },
  isLoadingHomeworks: false,
  reports: [],
  reportsPagination: {
    page: 1,
    limit: 10,
    limitOptions: [10, 25, 50]
  },
  isLoadingReports: false,
  firstPaymentPeriod: '',
  currentPaymentPeriod: ''
})

const mutations = {
  set(state, { type, payload }) {
    state[type] = payload
  },

  increaseVisibleStreams(state, type) {
    state.visibleCounter[type]++
  },

  sortStreams(state, value) {
    if (state.completed.streams && state.completed.streams.length) {
      const sortList = {
        lowGrade: (a, b) => compareByRating(a, b, 'low'),
        highGrade: (a, b) => compareByRating(a, b, 'high'),
        oldStreams: (a, b) => this.$moment(a.createdAtISO) - this.$moment(b.createdAtISO),
        newStreams: (a, b) => this.$moment(b.createdAtISO) - this.$moment(a.createdAtISO)
      }

      const compareByRating = (a, b, type) => {
        const nullComparison = (a.rating === null) - (b.rating === null)
        if (nullComparison !== 0) {
          return nullComparison
        }

        return type === 'low' ? a.rating - b.rating : b.rating - a.rating
      }

      state.completed.streams.sort(sortList[value])
    }
  },

  setHomeworks(state, { type, data }) {
    state.homeworks[type] = data
  },

  setHomeworksType(state, type) {
    state.selectedHomeworksType = type
  },

  setHomeworksPaginationPage(state, value) {
    state.homeworksPagination.page = value
  },

  setHomeworksPaginationLimit(state, value) {
    state.homeworksPagination.limit = value
  },

  setHomeworksSearchFields(state, { type, payload }) {
    state.homeworksSearchFields[type] = payload
  },

  setHomeworksQueryParams(state, payload) {
    Object.assign(state.homeworksQueryParams, payload)
  },

  setIsLoadingHomeworks(state, value) {
    state.isLoadingHomeworks = value
  },

  setReports(state, payload) {
    state.reports.push(payload)
  },

  setFirstPaymentPeriod(state, value) {
    state.firstPaymentPeriod = value
  },

  setCurrentPaymentPeriod(state, value) {
    state.currentPaymentPeriod = value
  },

  setReportsPaginationPage(state, value) {
    state.reportsPagination.page = value
  },

  setReportsPaginationLimit(state, value) {
    state.reportsPagination.limit = value
  },

  setIsLoadingReports(state, value) {
    state.isLoadingReports = value
  }
}

const actions = {
  async fetchStreamsData({ commit }, type) {
    try {
      const data = await this.$api.teacher.getStreamsData(type)

      if (type === 'completed') {
        await commit('set', {
          type: 'completed',
          payload: {
            averageGrade: data.averageGrade,
            streams: Object.values(data.streams)
          }
        })
      } else {
        await commit('set', {
          type: 'active',
          payload: [...data]
        })
      }
    } catch (e) {
      console.error(e)
    }
  },

  async homeworksRequest({ state, commit }, type) {
    try {
      const { homeworksQueryParams } = state
      const currentHomework = {
        pendings: 'pending',
        completed: 'completed',
        without_check: 'withoutCheck'
      }[type]

      const data = await this.$api.teacher.getHomeworks(type, homeworksQueryParams)

      if (homeworksQueryParams.course_id || homeworksQueryParams.search) {
        await commit('setHomeworksPaginationPage', 1)
      }

      await commit('setHomeworks', { type: currentHomework, data })
    } catch (e) {
      console.error(e)
    }
  },

  async fetchHomeworksData({ state, dispatch, commit }, init = false) {
    const maxHomeworksCount = 600
    const { selectedHomeworksType, homeworks, homeworksPagination } = state
    const currentType = {
      pending: 'pendings',
      completed: 'completed',
      withoutCheck: 'without_check'
    }[selectedHomeworksType]

    if (init) {
      if ((selectedHomeworksType === 'pending' && !homeworks.pending?.pending_homeworks?.length) ||
          (selectedHomeworksType === 'completed' && !homeworks.completed?.completed_homeworks?.length) ||
          (selectedHomeworksType === 'withoutCheck' && !homeworks.withoutCheck?.without_check_homeworks?.length)) {
        try {
          commit('setIsLoadingHomeworks', true)
          await commit('setHomeworksQueryParams', { limit: homeworksPagination.limit })
          await dispatch('homeworksRequest', currentType)
          commit('setIsLoadingHomeworks', false)

          await commit('setHomeworksQueryParams', { limit: maxHomeworksCount })
          await dispatch('homeworksRequest', currentType)
        } catch (e) {
          console.error(e)
        }
      }
    } else {
      try {
        await dispatch('homeworksRequest', currentType)
      } catch (e) {
        console.error(e)
      }
    }
  },

  async fetchSearchFields({ commit }, { field, id = null }) {
    const refreshHomeworkSearchField = (field) => {
      commit('setHomeworksSearchFields', {
        type: field,
        payload: []
      })
    }

    let fieldsUrl = 'courses'

    if (field === 'streams') {
      refreshHomeworkSearchField('streams')
      refreshHomeworkSearchField('lessons')
      fieldsUrl = `courses/${id}/streams`
    } else if (field === 'lessons') {
      refreshHomeworkSearchField('lessons')
      fieldsUrl = `streams/${id}/lessons`
    }

    try {
      const data = await this.$api.teacher.getSearchFields(fieldsUrl)

      if (data.length) {
        await commit('setHomeworksSearchFields', {
          type: field,
          payload: data.map(el => {
            return {
              label: el.title,
              value: el.id
            }
          })
        })
      }
    } catch (e) {
      console.error(e)
    }
  },

  async fetchSearchOptions({ commit }, search) {
    const nothingSearched = [{ value: 'Ничего не найдено', noSelect: true }]

    try {
      await commit('set', {
        type: 'homeworksSearchOptions',
        payload: []
      })

      const data = await this.$api.teacher.getSearchOptions(encodeURIComponent(search))

      await commit('set', {
        type: 'homeworksSearchOptions',
        payload: data && data.length ? data : nothingSearched
      })
    } catch (e) {
      console.error(e)
    }
  },

  async refreshHomeworkFilters({ commit, dispatch }) {
    try {
      await commit('setHomeworksQueryParams', { course_id: null, stream_id: null, lesson_id: null })

      const searchFields = ['streams', 'lessons']
      searchFields.forEach(field => {
        commit('setHomeworksSearchFields', {
          type: field,
          payload: []
        })
      })

      const types = ['pending', 'completed', 'withoutCheck']
      types.forEach(type => {
        commit('setHomeworks', { type, data: {} })
      })

      await dispatch('fetchHomeworksData', true)
    } catch (e) {
      console.error(e)
    }
  },

  async fetchReportsData({ state, commit }) {
    const { reports, currentPaymentPeriod } = state

    if (!reports.find(el => el.period === currentPaymentPeriod) || !reports.length) {
      await commit('setIsLoadingReports', true)

      try {
        const data = await this.$api.teacher.getReportsData(currentPaymentPeriod)

        const payload = {
          period: currentPaymentPeriod,
          ...data
        }

        await commit('setReports', payload)
      } catch (e) {
        console.error(e)
      }

      await commit('setIsLoadingReports', false)
    }
  },

  async fetchFirstPaymentPeriod({ state, commit }) {
    const { currentPaymentPeriod } = state

    try {
      const data = await this.$api.teacher.getFirstPaymentPeriod()

      const period = data || currentPaymentPeriod
      await commit('setFirstPaymentPeriod', period)
    } catch (e) {
      console.error(e)
    }
  }
}

const getters = {
  getCompletedVisibleStreams(state) {
    const { completed, visibleCounter } = state
    if (completed?.streams && completed.streams.length) {
      const endIndex = visibleCounter.completed * 20

      return completed.streams.slice(0, endIndex)
    }

    return []
  },

  getActiveVisibleStreams(state) {
    const { active, visibleCounter } = state
    if (active && active.length) {
      const endIndex = visibleCounter.active * 20

      return active.slice(0, endIndex)
    }

    return []
  },

  getCompletedStreams(state) {
    const { completed } = state

    return completed.streams ? completed.streams : []
  },

  getActiveStreams(state) {
    return state.active || []
  },

  getAverageGrade(state) {
    const { completed } = state

    return completed.averageGrade ? completed.averageGrade : ''
  },

  getCurrentHomeworks(state) {
    const { homeworks, selectedHomeworksType, homeworksPagination } = state

    const selectedTypeAnnex = {
      pending: 'pending_homeworks',
      completed: 'completed_homeworks',
      withoutCheck: 'without_check_homeworks'
    }[selectedHomeworksType]
    let currentHomeworks = []

    if (homeworks[selectedHomeworksType] && homeworks[selectedHomeworksType][selectedTypeAnnex]) {
      currentHomeworks = homeworks[selectedHomeworksType][selectedTypeAnnex]
    }

    if (currentHomeworks.length) {
      const startIndex = (homeworksPagination.page - 1) * homeworksPagination.limit
      const endIndex = homeworksPagination.page * homeworksPagination.limit

      return currentHomeworks.slice(startIndex, endIndex)
    }

    return []
  },

  getHomeworksPaginationParams(state) {
    const { homeworks, selectedHomeworksType, homeworksPagination } = state

    const selectedTypeTotal = {
      pending: 'pending_total_count',
      completed: 'completed_total_count',
      withoutCheck: 'without_check_total_count'
    }[selectedHomeworksType]
    let totalHomeworks = 0

    if (homeworks[selectedHomeworksType] && homeworks[selectedHomeworksType][selectedTypeTotal]) {
      totalHomeworks = homeworks[selectedHomeworksType][selectedTypeTotal]
    }

    return {
      selectedPage: homeworksPagination.page,
      limitOnPage: homeworksPagination.limit,
      totalPages: Math.ceil(totalHomeworks / homeworksPagination.limit),
      limitOptions: homeworksPagination.limitOptions
    }
  },

  getCoursesSearch(state) {
    return state.homeworksSearchFields.courses || []
  },

  getStreamSearch(state) {
    return state.homeworksSearchFields.streams || []
  },

  getLessonSearch(state) {
    return state.homeworksSearchFields.lessons || []
  },

  getCurrentReports(state) {
    const { reports, currentPaymentPeriod } = state

    return reports.find(el => el.period === currentPaymentPeriod) || {}
  },

  getReportsPaginationParams(state, getters) {
    const { reportsPagination } = state
    const currentReports = getters.getCurrentReports
    let totalReports = 0

    if (currentReports?.payments?.length) {
      totalReports = currentReports.payments.length
    }

    return {
      selectedPage: reportsPagination.page,
      limitOnPage: reportsPagination.limit,
      totalPages: Math.ceil(totalReports / reportsPagination.limit),
      limitOptions: reportsPagination.limitOptions
    }
  },
}

export default {
  state,
  actions,
  getters,
  mutations
}
